/**
 * Sessions hooks (TanStack Query) — tricorder-session's surface via the
 * gateway proxy from the cut-over on.
 */

import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import i18n from '@/i18n';
import { sessionsApi } from '../api/endpoints/sessions';
import { errorToast, refusalMessage } from '@/hooks/errorToast';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { ApiError, isTransportError, isServerFaultError } from '@/api/client';
import type {
  CreateSessionResponse,
  SessionResponse,
  MessageResponse,
  SessionPage,
  SessionStatus,
} from '../api/types_session';
import { QUERY_STALE_TIMES } from '@/lib/query-config';

/**
 * The session detail cache: the session and a page of its messages, read as
 * two calls and cached as one entry (GET /sessions/{id} and
 * GET /sessions/{id}/messages are two reads of one resource upstream).
 */
export type SessionDetail = {
  session: SessionResponse;
  messages: SessionPage<MessageResponse>;
};

/**
 * Snapshot of every variant of `sessionKeys.lists()` for rollback.
 */
type SessionListSnapshot = ReadonlyArray<
  readonly [readonly unknown[], SessionResponse[] | undefined]
>;

function snapshotSessionLists(qc: QueryClient): SessionListSnapshot {
  return qc.getQueriesData<SessionResponse[]>({
    queryKey: sessionKeys.lists(),
  });
}

function restoreSessionLists(
  qc: QueryClient,
  snapshot: SessionListSnapshot,
): void {
  for (const [key, data] of snapshot) {
    qc.setQueryData(key, data);
  }
}

/**
 * Query keys for sessions.
 */
export const sessionKeys = {
  all: ['sessions'] as const,
  lists: () => [...sessionKeys.all, 'list'] as const,
  list: (status?: string) => [...sessionKeys.lists(), { status }] as const,
  details: () => [...sessionKeys.all, 'detail'] as const,
  detail: (id: string) => [...sessionKeys.details(), id] as const,
};

/**
 * Get list of user's sessions (most recent activity first). `status` narrows
 * to one lifecycle status; absent lists both.
 */
export function useSessions(
  status?: SessionStatus,
  options?: Omit<
    UseQueryOptions<SessionResponse[]>,
    'queryKey' | 'queryFn'
  >,
) {
  return useAuthenticatedQuery({
    queryKey: sessionKeys.list(status),
    queryFn: async () => {
      const page = await sessionsApi.list(status);
      return page.items;
    },
    staleTime: QUERY_STALE_TIMES.sessions,
    retry: 1,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    ...options,
  });
}

/**
 * Get single session with its messages.
 */
export function useSession(
  id: string,
  options?: Omit<
    UseQueryOptions<SessionDetail, ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useAuthenticatedQuery<SessionDetail, ApiError>({
    queryKey: sessionKeys.detail(id),
    queryFn: async (): Promise<SessionDetail> => {
      const [session, messages] = await Promise.all([
        sessionsApi.get(id),
        sessionsApi.messages(id),
      ]);
      return { session, messages };
    },
    enabled: !!id,
    staleTime: QUERY_STALE_TIMES.sessionDetail,
    retry: 1,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    ...options,
  });
}

/**
 * The session's messages after a seq (the catch-up read). Keyed under the
 * session's detail key, so the event stream's invalidation of that key on
 * turn.completed / turn.failed refetches it.
 */
export function useSessionMessages(id: string, after: number) {
  return useAuthenticatedQuery({
    queryKey: [...sessionKeys.detail(id), 'messages', { after }],
    queryFn: async () => (await sessionsApi.messages(id, after)).items,
    enabled: !!id && after > 0,
    staleTime: 0,
  });
}

/**
 * Module-level singleflight for session creation, keyed by the create
 * identity, so a double-tap coalesces and two different taps never share one
 * request.
 */
const _createSessionPending = new Map<string, Promise<CreateSessionResponse>>();

export interface CreateSessionParams {
  agentSlug: string;
  /** The user's first message. Required: creating a session runs it. */
  message: string;
  title?: string;
  /** Cites the party the session is about — `{type: 'patient', id}` for a
   *  patient-bound conversation. */
  subject?: { type: string; id: string };
  idempotencyKey?: string;
}

function createSessionDedupKey(params: CreateSessionParams): string {
  return `${params.agentSlug}::${params.subject?.type ?? ''}:${params.subject?.id ?? ''}::${params.message}`;
}

/**
 * createSessionOnce creates a session and RUNS it, through the singleflight
 * above. Creating takes the agent and the first message (there is no idle
 * create upstream).
 */
export function createSessionOnce(params: CreateSessionParams): Promise<CreateSessionResponse> {
  const key = createSessionDedupKey(params);
  const inFlight = _createSessionPending.get(key);
  if (inFlight) return inFlight;
  const pending = sessionsApi
    .create(params.agentSlug, params.message, {
      title: params.title,
      subject: params.subject,
      idempotencyKey: params.idempotencyKey,
    })
    .finally(() => {
      _createSessionPending.delete(key);
    });
  _createSessionPending.set(key, pending);
  return pending;
}

interface CreateSessionContext {
  previousLists: SessionListSnapshot;
}

/**
 * Create new session — and RUN it (createSessionOnce), with the session
 * lists' snapshot and refetch around it. The params are the compose the user
 * typed plus the agent to open.
 */
export function useCreateSession() {
  const queryClient = useQueryClient();

  return useMutation<
    SessionResponse,
    Error,
    CreateSessionParams,
    CreateSessionContext
  >({
    mutationFn: async (params: CreateSessionParams) => (await createSessionOnce(params)).session,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: sessionKeys.lists() });
      const previousLists = snapshotSessionLists(queryClient);
      return { previousLists };
    },
    onError: (err, _vars, context) => {
      if (context?.previousLists) {
        restoreSessionLists(queryClient, context.previousLists);
      }
      const code = err instanceof ApiError ? err.code : undefined;
      errorToast({
        context: 'createSession',
        description: refusalMessage(code, i18n.t('session.createError')),
        errorCode: code,
        error: err,
        reportMessage: err instanceof ApiError ? err.message : undefined,
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}

/**
 * Update session (rename): a full replace of title and status, never
 * conditional.
 */
interface UpdateSessionVariables {
  id: string;
  title: string;
}

interface UpdateSessionContext {
  previousLists: SessionListSnapshot;
  previousDetail: unknown;
}

export function useUpdateSession() {
  const queryClient = useQueryClient();

  return useMutation<
    SessionResponse,
    Error,
    UpdateSessionVariables,
    UpdateSessionContext
  >({
    mutationFn: ({ id, title }) => sessionsApi.rename(id, title),
    onMutate: async ({ id, title }) => {
      await queryClient.cancelQueries({ queryKey: sessionKeys.lists() });
      await queryClient.cancelQueries({ queryKey: sessionKeys.detail(id) });

      const previousLists = snapshotSessionLists(queryClient);
      const previousDetail = queryClient.getQueryData(sessionKeys.detail(id));

      queryClient.setQueriesData<SessionResponse[]>(
        { queryKey: sessionKeys.lists() },
        (old) =>
          old?.map((s) => (s.id === id ? { ...s, title } : s)) ?? [],
      );

      queryClient.setQueryData(sessionKeys.detail(id), (old: unknown) => {
        if (typeof old !== 'object' || old === null) return old;
        const detail = old as SessionDetail;
        return { ...detail, session: { ...detail.session, title } };
      });

      return { previousLists, previousDetail };
    },
    onError: (_err, variables, context) => {
      if (context?.previousLists) {
        restoreSessionLists(queryClient, context.previousLists);
      }
      if (context && 'previousDetail' in context) {
        queryClient.setQueryData(
          sessionKeys.detail(variables.id),
          context.previousDetail,
        );
      }
      errorToast({
        context: 'renameSession',
        description: i18n.t('session.renameError'),
      });
    },
    onSettled: (_data, _err, variables) => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: sessionKeys.detail(variables.id),
      });
    },
  });
}

/**
 * Erase session permanently.
 */
interface DeleteSessionContext {
  previousLists: SessionListSnapshot;
}

export function useDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string, DeleteSessionContext>({
    mutationFn: async (id: string) => {
      await sessionsApi.delete(id);
    },
    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey: sessionKeys.lists() });

      const previousLists = snapshotSessionLists(queryClient);

      queryClient.setQueriesData<SessionResponse[]>(
        { queryKey: sessionKeys.lists() },
        (old) => old?.filter((s) => s.id !== deletedId) ?? []
      );

      queryClient.removeQueries({ queryKey: sessionKeys.detail(deletedId) });

      return { previousLists };
    },
    onError: (error, _deletedId, context) => {
      if (context?.previousLists) {
        restoreSessionLists(queryClient, context.previousLists);
      }
      let detailKey = 'session.deleteError';
      if (error instanceof ApiError) {
        if (error.status === 403) {
          detailKey = 'session.deleteForbidden';
        } else if (isTransportError(error) || isServerFaultError(error)) {
          detailKey = 'session.deleteConnection';
        }
      }
      errorToast({
        context: 'deleteSession',
        description: i18n.t(detailKey),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}

/**
 * Archive session. An archived session takes no message; open a new one.
 */
interface ArchiveSessionContext {
  previousLists: SessionListSnapshot;
}

export function useArchiveSession() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; title: string }, ArchiveSessionContext>({
    mutationFn: async ({ id, title }) => {
      await sessionsApi.archive(id, title);
    },
    onMutate: async ({ id: archivedId }) => {
      await queryClient.cancelQueries({ queryKey: sessionKeys.lists() });
      const previousLists = snapshotSessionLists(queryClient);

      queryClient.setQueriesData<SessionResponse[]>(
        { queryKey: sessionKeys.lists() },
        (old) => old?.filter((s) => s.id !== archivedId) ?? [],
      );
      queryClient.removeQueries({ queryKey: sessionKeys.detail(archivedId) });

      return { previousLists };
    },
    onError: (_err, _archivedId, context) => {
      if (context?.previousLists) {
        restoreSessionLists(queryClient, context.previousLists);
      }
      errorToast({
        context: 'archiveSession',
        description: i18n.t('session.archiveError'),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}

/**
 * Share a session with another user, group or unit — the session's own shares
 * sub-resource upstream ({principal_type, principal_id, role, expires_at?}).
 */
interface ShareSessionVariables {
  sessionId: string;
  principalType: 'user' | 'group';
  principalId: string;
  role: 'viewer' | 'editor';
  expiresAt?: string;
}

export function useShareSession() {
  return useMutation<void, Error, ShareSessionVariables>({
    mutationFn: async ({ sessionId, principalType, principalId, role, expiresAt }) => {
      await sessionsApi.share(sessionId, {
        principal_type: principalType,
        principal_id: principalId,
        role,
        ...(expiresAt ? { expires_at: expiresAt } : {}),
      });
    },
    onError: (_err) => {
      errorToast({
        context: 'shareSession',
        description: i18n.t('session.sharing.shareError', {
          defaultValue: 'Could not share session. Please try again.',
        }),
      });
    },
  });
}
